import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, FolderKanban, Users } from 'lucide-react';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonText } from '@/components/Skeleton';
import { obterWorkload, type MembroWorkload } from '@/features/projetos/projetos.api';
import { cn } from '@/lib/cn';

function fmtData(ep: number | null) {
  if (!ep) return '—';
  return new Date((ep - 1) * 60_000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function Avatar({ nome }: { nome: string }) {
  const ini = nome.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
  return (
    <span className="grid h-8 w-8 flex-none place-items-center rounded-full gradient-primary text-[11px] font-bold text-white shrink-0">
      {ini || '?'}
    </span>
  );
}

function BarraCarga({ total, done, overdue }: { total: number; done: number; overdue: number }) {
  const donePct = total > 0 ? Math.round((done / total) * 100) : 0;
  const overduePct = total > 0 ? Math.round((overdue / total) * 100) : 0;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted flex">
      <div className="h-full bg-green-500 transition-all" style={{ width: `${donePct}%` }} />
      <div className="h-full bg-primary transition-all" style={{ width: `${Math.max(0, 100 - donePct - overduePct)}%` }} />
      <div className="h-full bg-destructive transition-all" style={{ width: `${overduePct}%` }} />
    </div>
  );
}

function CartaoMembro({ membro }: { membro: MembroWorkload }) {
  const [expandido, setExpandido] = useState(false);
  const nome = membro.user.displayName ?? membro.user.name;
  const nowMin = Math.floor(Date.now() / 60_000);
  const ativas = membro.tasks.filter((t) => t.progress < 100);

  return (
    <div className="rounded-xl border border-border bg-background overflow-hidden">
      <button
        type="button"
        onClick={() => setExpandido((v) => !v)}
        className="flex w-full items-center gap-3 p-4 hover:bg-muted/20 transition-colors text-left"
      >
        <Avatar nome={nome} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{nome}</span>
            {membro.overdue > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                <AlertTriangle className="h-3 w-3" /> {membro.overdue} atrasada{membro.overdue > 1 ? 's' : ''}
              </span>
            )}
            {membro.critical > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                {membro.critical} crítica{membro.critical > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="mt-1.5 space-y-1">
            <BarraCarga total={membro.total} done={membro.done} overdue={membro.overdue} />
            <div className="flex gap-3 text-[10px] text-muted-foreground">
              <span>{membro.total} tarefas</span>
              <span className="text-green-600 dark:text-green-400">{membro.done} concluídas</span>
              <span>{ativas.length} em aberto</span>
            </div>
          </div>
        </div>
        {expandido ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
      </button>

      {expandido && (
        <div className="border-t border-border divide-y divide-border">
          {membro.tasks.map((t) => {
            const atrasada = t.progress < 100 && (t.finishAt ?? 0) < nowMin;
            return (
              <div key={t.id} className={cn('px-4 py-2.5 flex items-center gap-3', atrasada && 'bg-destructive/3')}>
                <div
                  className={cn(
                    'h-1.5 w-1.5 rounded-full shrink-0',
                    t.progress === 100 ? 'bg-green-500' : atrasada ? 'bg-destructive' : t.isCritical ? 'bg-amber-500' : 'bg-primary',
                  )}
                />
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm truncate', atrasada && 'text-destructive')}>{t.name}</p>
                  <Link
                    to="/projetos/$projetoId"
                    params={{ projetoId: t.project.id }}
                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FolderKanban className="h-3 w-3" />
                    {t.project.number}
                  </Link>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-[11px] text-muted-foreground">
                  {t.finishAt && (
                    <span className={cn(atrasada && 'text-destructive font-medium')}>
                      {fmtData(t.finishAt)}
                    </span>
                  )}
                  <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn('h-full rounded-full', t.progress === 100 ? 'bg-green-500' : 'bg-primary')}
                      style={{ width: `${t.progress}%` }}
                    />
                  </div>
                  <span className="w-7 tabular-nums text-right">{t.progress}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function WorkloadPage() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['projetos', 'workload'],
    queryFn: obterWorkload,
  });

  const membros = data ?? [];
  const totalTarefas = membros.reduce((s, m) => s + m.total, 0);
  const totalAtrasadas = membros.reduce((s, m) => s + m.overdue, 0);

  return (
    <div className="mx-auto max-w-[1400px] space-y-4">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <div>
          <h1 className="text-base font-semibold">Carga de trabalho</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Tarefas de projetos ativos por responsável
          </p>
        </div>
        {!isLoading && membros.length > 0 && (
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span><span className="font-semibold text-foreground tabular-nums">{membros.length}</span> pessoas</span>
            <span><span className="font-semibold text-foreground tabular-nums">{totalTarefas}</span> tarefas</span>
            {totalAtrasadas > 0 && (
              <span><span className="font-semibold text-destructive tabular-nums">{totalAtrasadas}</span> atrasadas</span>
            )}
          </div>
        )}
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border border-border p-4 space-y-2">
              <SkeletonText className="h-4 w-1/3" />
              <SkeletonText className="h-2 w-full" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <ErrorState error={null} onRetry={() => void refetch()} />
      ) : membros.length === 0 ? (
        <div className="rounded-xl border border-border bg-muted/10 px-6 py-12 text-center">
          <Users className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
          <p className="text-sm font-medium text-muted-foreground">Nenhuma tarefa atribuída em projetos ativos.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {[...membros].sort((a, b) => b.overdue - a.overdue || b.total - a.total).map((m) => (
            <CartaoMembro key={m.user.id} membro={m} />
          ))}
        </div>
      )}
    </div>
  );
}
